import React, { useMemo } from 'react';

import useUI from 'hooks/use-ui';
import { useCommerceComponents } from 'src/theme';
import type { CommerceComponents } from 'src/theme/provider/commerce-components/context';
import type { StoreFrontCarouselTitleProps } from '@dutchiesdk/ecommerce-extensions-sdk';

/* eslint-disable @typescript-eslint/naming-convention */
export enum RemoteComponentSlot {
  Header = 'header',
  Navigation = 'navigation',
  Footer = 'footer',

  Hero = 'hero',
  ProductDetails = 'product-details',
  ListPageHero = 'list-page-hero',

  LoyaltyWidget = 'loyalty-widget',
  LoginWidget = 'login-widget',
  CategoryTiles = 'category-tiles',
  SpecialsCategoryTiles = 'specials-category-tiles',

  SpecialsPageHeader = 'specials-page-header',
}
/* eslint-enable @typescript-eslint/naming-convention */

type RemoteComponentOptions<P extends object> = {
  fallback?: React.FC<P>;
};

type CommerceComponentSlot = keyof CommerceComponents['components'];

function remoteComponentSlotToCommerceSlot(slot: RemoteComponentSlot): CommerceComponentSlot | null {
  switch (slot) {
    case RemoteComponentSlot.Header:
      return 'StoreFrontHeader';
    case RemoteComponentSlot.Navigation:
      return 'StoreFrontNavigation';
    case RemoteComponentSlot.Footer:
      return 'StoreFrontFooter';
    case RemoteComponentSlot.ProductDetails:
      return 'ProductDetailsPrimary';
    case RemoteComponentSlot.Hero:
      return 'StoreFrontHero';
    case RemoteComponentSlot.ListPageHero:
      return 'ListPageHero';
    case RemoteComponentSlot.LoyaltyWidget:
      return 'StoreFrontLoyaltyWidget';
    case RemoteComponentSlot.LoginWidget:
      return 'StoreFrontLoginWidget';
    case RemoteComponentSlot.CategoryTiles:
      return 'StoreFrontCategoryTiles';
    case RemoteComponentSlot.SpecialsCategoryTiles:
      return 'StoreFrontSpecialsCategoryTiles';
    case RemoteComponentSlot.SpecialsPageHeader:
      return 'SpecialsPageHeader';
  }
  return null;
}

function EmptyComponent(): JSX.Element {
  return <></>;
}

/** Resolves the remote title registered for one stable carousel id. */
export function useRemoteCarouselTitle(
  carouselId: string | undefined,
  options: RemoteComponentOptions<StoreFrontCarouselTitleProps> = {}
): React.ComponentType<StoreFrontCarouselTitleProps> {
  const { findCarouselTitle } = useCommerceComponents();
  const { isStoreFront, isKiosk } = useUI();
  const fallback = options.fallback ?? EmptyComponent;

  return useMemo(() => {
    if (!carouselId || (!isStoreFront && !isKiosk)) {
      return fallback;
    }
    return findCarouselTitle({ carouselId, fallback });
  }, [carouselId, fallback, findCarouselTitle, isKiosk, isStoreFront]);
}

/**
 * Resolves a remote component for a given slot.
 * Resolution order: commerce component (SDK) → fallback → empty component.
 */
export function useRemoteComponent<T extends object>(
  slot: RemoteComponentSlot,
  options: RemoteComponentOptions<T> = {}
): React.ComponentType<T & { fallback?: React.ComponentType<T> }> {
  const { components } = useCommerceComponents();
  const { isStoreFront, isKiosk } = useUI();

  const fallback: React.ComponentType = options.fallback ?? EmptyComponent;

  const commerceSlot = remoteComponentSlotToCommerceSlot(slot);
  const ResolvedComponent: React.FC<T> = useMemo(() => {
    if (!commerceSlot || (!isStoreFront && !isKiosk)) {
      return fallback as React.FC<T>;
    }
    return components[commerceSlot]({ fallback });
  }, [components, commerceSlot, isStoreFront, isKiosk, fallback]);

  return ResolvedComponent;
}
